import { useQuery } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { deadlinesQueryKeys } from '../../pages/documents-archive/deadlines/deadline-query-keys';
import { ConsultantDeadlinesService } from '../../services/consultant/consultant-deadlines.service';
import type { IConsultantDeadlinesService } from '../../services/consultant/consultant-deadlines.service.interface';
import { DeadlineService, type IDeadlineService } from '../../services/deadline/deadline.service';
import { consultantDeadlineQueryKeys } from '../consultant/use-consultant-deadlines';
import { useAuthUser } from '../use-auth-user';

const SUMMARY_STALE_TIME_MS = 30_000;

/** `documentTypeId` null = no request (no type, "Altro", personal type without a person). */
export interface SameTypeSummaryRequest {
  documentTypeId: string | null;
  /** Personal types: only the deadlines of this person count. */
  assigneeUserId?: string;
}

/** Avviso informativo del dialog del cliente. */
export function useTenantSameTypeSummary({
  documentTypeId,
  assigneeUserId,
}: SameTypeSummaryRequest) {
  const service = useInjection<IDeadlineService>(DeadlineService);
  const typeId = documentTypeId ?? '';
  return useQuery({
    queryKey: deadlinesQueryKeys.sameTypeSummary(typeId, assigneeUserId ?? ''),
    queryFn: () => service.getSameTypeSummary(typeId, assigneeUserId),
    enabled: typeId !== '',
    staleTime: SUMMARY_STALE_TIME_MS,
    select: (response) => response.items,
  });
}

/** Avviso informativo del dialog del consulente, per i clienti scelti. */
export function useConsultantSameTypeSummary(
  tenantIds: readonly string[],
  { documentTypeId, assigneeUserId }: SameTypeSummaryRequest,
) {
  const service = useInjection<IConsultantDeadlinesService>(ConsultantDeadlinesService);
  const consultantUserId = useAuthUser()?.sub ?? '';
  const typeId = documentTypeId ?? '';
  const sortedTenantIds = [...tenantIds].sort();
  return useQuery({
    queryKey: consultantDeadlineQueryKeys.sameTypeSummary(consultantUserId, {
      documentTypeId: typeId,
      tenantIds: sortedTenantIds.join(','),
      assigneeUserId: assigneeUserId ?? '',
    }),
    queryFn: () => service.getSameTypeSummary(sortedTenantIds, typeId, assigneeUserId),
    enabled: typeId !== '' && sortedTenantIds.length > 0,
    staleTime: SUMMARY_STALE_TIME_MS,
    select: (response) => response.items,
  });
}
